import { useState, useEffect } from 'react';
import api from '../../config/axios';
import { MdClose } from 'react-icons/md';
import { useLanguage } from '../../context/LanguageContext';

const CustomerCreateSupportCaseModal = ({ isOpen, onClose, onSubmit }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    contract_id: '',
    product_id: '',
    sla_group_id: '',
    sla_id: '',
    subject: '',
    detail: ''
  });
  
  const [contracts, setContracts] = useState([]);
  const [products, setProducts] = useState([]);
  const [slaGroups, setSlaGroups] = useState([]);
  const [slaDefinitions, setSlaDefinitions] = useState([]);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  useEffect(() => {
    // Get current user from localStorage
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      setUser(userData);
      // Fetch contracts for this customer
      if (userData.customer_id) {
        fetchContracts(userData.customer_id);
      }
    }
  }, []);
  
  const fetchContracts = async (customerId) => {
    try {
      setIsLoading(true);
      const response = await api.get('/customer-contracts', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: { customer_id: customerId }
      });
      setContracts(response.data || []);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      setContracts([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchProducts = async (contractId) => {
    try {
      setIsLoading(true);
      const response = await api.get('/contract-products', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: { contract_id: contractId }
      });
      setProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchSlaGroups = async (productId) => {
    try {
      setIsLoading(true);
      
      // Find the selected product from current products state
      const selectedProduct = products.find(p => p.id === parseInt(productId));
      if (selectedProduct && selectedProduct.sla_group_id) {
        // Get SLA group details
        const groupResponse = await api.get(`/sla/${selectedProduct.sla_group_id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        if (groupResponse.data.success) {
          setSlaGroups([{
            id: selectedProduct.sla_group_id,
            group_name: groupResponse.data.sla.sla_group_name
          }]);
        }
      } else {
        setSlaGroups([]);
        setSlaDefinitions([]);
      }
    } catch (error) {
      console.error('Error fetching SLA groups:', error);
      setSlaGroups([]);
      setSlaDefinitions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSlaDefinitions = async (slaGroupId) => {
    try {
      setIsLoading(true);
      
      const response = await api.get(`/sla/${slaGroupId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.success && response.data.sla.definitions) {
        setSlaDefinitions(response.data.sla.definitions);
      } else {
        setSlaDefinitions([]);
      }
    } catch (error) {
      console.error('Error fetching SLA definitions:', error);
      setSlaDefinitions([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Handle dependent dropdowns
    if (name === 'contract_id') {
      fetchProducts(value);
      setFormData(prev => ({ 
        ...prev, 
        product_id: '',
        sla_group_id: '',
        sla_id: ''
      }));
      setProducts([]);
      setSlaGroups([]);
      setSlaDefinitions([]);
    } else if (name === 'product_id') {
      if (value) {
        fetchSlaGroups(value);
      } else {
        setSlaGroups([]);
        setSlaDefinitions([]);
      }
      setFormData(prev => ({ 
        ...prev, 
        sla_group_id: '',
        sla_id: ''
      }));
    } else if (name === 'sla_group_id') {
      if (value) {
        fetchSlaDefinitions(value);
      } else {
        setSlaDefinitions([]);
      }
      setFormData(prev => ({ 
        ...prev, 
        sla_id: ''
      }));
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!user) {
      console.error('User not found');
      return;
    }
    
    // Create the support case object with the correct fields
    const supportCase = {
      customer_id: user.customer_id,
      product_id: formData.product_id,
      sla_id: formData.sla_id,
      created_by: user.id,
      source: 'Portal', // Otomatik Portal olarak ayarla
      case_owner: null, // Not Assigned Yet
      subject: formData.subject,
      detail: formData.detail,
      case_status: 'OPEN'
    };
    
    onSubmit(supportCase);
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center p-1 sm:p-2 lg:p-4">
      <div className="relative mx-auto w-full max-w-2xl sm:max-w-3xl shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
        {/* Header */}
        <div className="bg-[#272c39] text-white px-2 sm:px-3 lg:px-6 py-2 sm:py-3 lg:py-4 flex justify-between items-center">
          <h3 className="text-xs sm:text-sm lg:text-lg font-medium">{t('customerCreateSupportModal.title')}</h3>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200"
          >
            <MdClose size={16} className="sm:hidden" />
            <MdClose size={20} className="hidden sm:block lg:hidden" />
            <MdClose size={24} className="hidden lg:block" />
          </button>
        </div>
        
        <div className="p-2 sm:p-3 lg:p-6 xl:p-8">
          {isLoading && (
            <div className="absolute inset-0 bg-white dark:bg-gray-800 bg-opacity-75 dark:bg-opacity-75 flex items-center justify-center z-10">
              <div className="inline-block h-6 w-6 sm:h-8 sm:w-8 rounded-full border-4 border-gray-300 border-t-blue-600 animate-spin"></div>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 lg:gap-4">
            {/* Contract */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5 sm:mb-1">{t('customerCreateSupportModal.contract')} *</label>
              <select
                name="contract_id"
                value={formData.contract_id}
                onChange={handleChange}
                required
                className="w-full px-1.5 sm:px-2 lg:px-3 py-1 sm:py-1.5 lg:py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
              >
                <option value="">{t('customerCreateSupportModal.selectContract')}</option>
                {contracts.map(contract => (
                  <option key={contract.id} value={contract.id}>
                    {contract.contract_name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Product */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5 sm:mb-1">{t('customerCreateSupportModal.product')} *</label>
              <select
                name="product_id"
                value={formData.product_id}
                onChange={handleChange}
                required
                disabled={!formData.contract_id}
                className="w-full px-1.5 sm:px-2 lg:px-3 py-1 sm:py-1.5 lg:py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-600 text-xs sm:text-sm"
              >
                <option value="">{t('customerCreateSupportModal.selectProduct')}</option>
                {products.map(product => (
                  <option key={product.id} value={product.id}>
                    {product.product_name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* SLA Group */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5 sm:mb-1">{t('customerCreateSupportModal.slaGroup')} *</label>
              <select
                name="sla_group_id"
                value={formData.sla_group_id}
                onChange={handleChange}
                required
                disabled={!formData.product_id || slaGroups.length === 0}
                className="w-full px-1.5 sm:px-2 lg:px-3 py-1 sm:py-1.5 lg:py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-600 text-xs sm:text-sm"
              >
                <option value="">{t('customerCreateSupportModal.selectSlaGroup')}</option>
                {slaGroups.map(slaGroup => (
                  <option key={slaGroup.id} value={slaGroup.id}>
                    {slaGroup.group_name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* SLA Definition (Severity) */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5 sm:mb-1">{t('customerCreateSupportModal.priority')} *</label>
              <select
                name="sla_id"
                value={formData.sla_id}
                onChange={handleChange}
                required
                disabled={!formData.sla_group_id || slaDefinitions.length === 0}
                className="w-full px-1.5 sm:px-2 lg:px-3 py-1 sm:py-1.5 lg:py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-600 text-xs sm:text-sm"
              >
                <option value="">{t('customerCreateSupportModal.selectPriority')}</option>
                {slaDefinitions.map(definition => (
                  <option key={definition.id} value={definition.id}>
                    {definition.sla_severity}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Subject */}
            <div className="col-span-1 sm:col-span-2">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5 sm:mb-1">{t('customerCreateSupportModal.subject')} *</label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className="w-full px-1.5 sm:px-2 lg:px-3 py-1 sm:py-1.5 lg:py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                placeholder={t('customerCreateSupportModal.subjectPlaceholder')}
              />
            </div>
            
            {/* Case Detail */}
            <div className="col-span-1 sm:col-span-2">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5 sm:mb-1">{t('customerCreateSupportModal.detail')} *</label>
              <textarea
                name="detail"
                value={formData.detail}
                onChange={handleChange}
                required
                rows={3}
                className="w-full px-1.5 sm:px-2 lg:px-3 py-1 sm:py-1.5 lg:py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                placeholder={t('customerCreateSupportModal.detailPlaceholder')}
              />
            </div>
            
            {/* Buttons */}
            <div className="col-span-1 sm:col-span-2 flex flex-col sm:flex-row justify-end gap-1.5 sm:gap-2 lg:gap-3 mt-2 sm:mt-3 lg:mt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-2 sm:px-3 lg:px-4 py-1 sm:py-1.5 lg:py-2 text-gray-700 dark:text-white bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 text-xs sm:text-sm lg:text-base"
              >
                {t('customerCreateSupportModal.cancel')}
              </button>
              <button
                type="submit"
                className="px-2 sm:px-3 lg:px-4 py-1 sm:py-1.5 lg:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-xs sm:text-sm lg:text-base"
                disabled={isLoading}
              >
                {isLoading ? t('customerCreateSupportModal.creating') : t('customerCreateSupportModal.createTicket')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CustomerCreateSupportCaseModal;